import CompositeError from '../../ksdk/CompositeError';
import LocalState from '../../state/LocalState';
import Reporter from '../../common/Reporter/Reporter';
import handleWebsocketConnect from './handleWebsocketConnect';
import logger from '../../log';
import { ApiEvents } from '@kollective-frontend/ksdk-api';
import {
  InternalEvents,
  DiagnosticEventData,
  DiagnosticEvent
} from '../../events';
import { EventData } from '../../common/interfaces';
import { generateSessionDiagnosticArgs } from './GenerateDiagnostic';
import { sendSessionDiagnostic } from './sendDiagnostic';
const log = logger.getLogger(__filename);

const { WEBSOCKET_CONNECT } = ApiEvents;
const { DIAGNOSTIC } = InternalEvents;
type CoveredApiEvents = ApiEvents.WEBSOCKET_CONNECT | InternalEvents.DIAGNOSTIC;

export type DiagnosticBinderFunction = (
  e: EventData,
  state: LocalState
) => Promise<void>;

// export for testing
export const getMessageFromDiagnosticEventData = (
  item: DiagnosticEventData
): string | undefined => {
  const { error, message } = item;
  if (typeof error !== 'object' || error === null) {
    return message;
  }
  const messages: string[] = [];
  if (typeof message === 'string') {
    messages.push(message);
  }
  const err = error as Error;
  if (err?.message) {
    messages.push(err.message);
  }
  if (messages.length > 0) {
    return messages.join(', ');
  }
  return undefined;
};

// We don't know what the error field will be so we want to handle the
// interesting fields from Error, CompositeError, Event, CloseEvent,
// RequestError, or XMLHttpRequest
const fields: string[] = [
  'code',
  'reason',
  'responseText',
  'status',
  'statusCode',
  'type',
  'wasClean'
];

export const getData = (
  item: DiagnosticEventData
): Record<string, unknown> | undefined => {
  const { error, data: indata } = item;
  if (typeof error === 'undefined' || error == null) {
    return indata;
  }
  // If ksdk/CompositeError... errors: Error[];
  //
  const data: Record<string, unknown> = indata ?? {};
  if (typeof error === 'string') {
    data.error = error;
    return data;
  }
  if (typeof error !== 'object') {
    data.error = `${error}`;
    return data;
  }
  const record = error as Record<string, unknown>;
  if (record.name) {
    const key = data.name ? 'typeName' : 'name';
    data[key] = record.name;
  }
  const proto = record.__proto__ as Record<string, string>;
  if (proto?.name) {
    const key = data.name ? 'protoName' : 'name';
    data[key] = proto.name;
  }
  fields.forEach(key => {
    const value = record[key];
    if (typeof value !== 'undefined' && value !== null) {
      const k = data[key] ? `error_${key}` : key;
      data[k] = record[key];
    }
  });
  // error message is handled by message, handle other error fields
  if (typeof record?.stack === 'string') {
    // The first 150 characters of the stack should be enough.
    data.errorStack = record.stack.substring(0, 150);
  }
  if (Array.isArray((error as CompositeError).errors)) {
    const compositeError = error as CompositeError;
    const errors: Error[] = [];
    compositeError.errors.forEach(err => {
      const { message, stack, name } = err;
      const proto = record.__proto__ as Record<string, string>;
      const e: Error = { message, name };
      if (stack) {
        e.stack = stack.substring(0, 150);
      }
      if (proto?.name) {
        (error as Record<string, string>).protoName = proto.name;
      }
      errors.push(e);
    });
    if (errors.length > 0) {
      data.errors = errors;
    }
  }
  return data;
};

export const handleDiagnosticEvent = async (
  event: DiagnosticEvent,
  state: LocalState
) => {
  log.info(event);
  const message = getMessageFromDiagnosticEventData(event.data);
  const data = getData(event.data);
  const record = { ...event.data, data, message, state };
  const diag = generateSessionDiagnosticArgs(record);
  await sendSessionDiagnostic(diag);
};

export const binders: Record<CoveredApiEvents, unknown> = {
  [WEBSOCKET_CONNECT]: handleWebsocketConnect,
  [DIAGNOSTIC]: handleDiagnosticEvent
};

export default function bindDiagnosticListeners(reporter: Reporter): void {
  const { eventEmitter: emitter, localState: state } = reporter;
  Object.entries(binders).forEach(([key, f]) => {
    const func = f as DiagnosticBinderFunction;
    log.trace(`binding ${key} diagnostic listener`);
    emitter.addEventListener(key as ApiEvents, (e: EventData) =>
      func(e, state)
    );
  });
}
